import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ChecklistSideBar from './ChecklistSideBar/ChecklistSideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import ChecklistService from '../../../../../../AppSettings/services/ChecklistService';
import HttpService from '../../../../../../AppSettings/services/HttpService';
import Table from '../../../../../../Components/SecondaryTable/SecondaryTable';
import Delete from '../../../../../../Components/Delete/Delete';
import { ChecklistItem } from '../../../../../../Models/Checklist/ChecklistModels';
import type { PaginationProps } from '../../../../../../Models/Table/TableModels';
import { detailRouteBase } from '../../../../../utils/detailRoute';

interface ChecklistTableProps {
  data: ChecklistItem[];
  pageSize?: number;
  pagination?: PaginationProps;
  onChecklistDeleted?: () => void;
}

const ChecklistTable: React.FC<ChecklistTableProps> = ({ data, pageSize = 10, pagination, onChecklistDeleted }) => {
  const { matterId } = useParams<{ matterId: string }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistItem | null>(null);
  const [checklistData, setChecklistData] = useState<ChecklistItem[]>(data);

  useEffect(() => {
    setChecklistData(data || []);
  }, [data]);

  const openSidebar = (checklist: ChecklistItem): void => {
    setSelectedChecklist(checklist);
    setIsSidebarOpen(true);
  };

  const closeSidebar = (): void => {
    setIsSidebarOpen(false);
    setSelectedChecklist(null);
  };

  const handleStatusToggle = async (checklistId: number | undefined, currentStatus: boolean): Promise<void> => {
    if (!checklistId) {
      console.error('Checklist ID is undefined');
      return;
    }

    const newStatus = !currentStatus;

    // Optimistically update the state
    setChecklistData((prevData) =>
      prevData.map((item) =>
        item.checklistid === checklistId ? { ...item, status: newStatus } : item
      )
    );

    try {
      const response = await ChecklistService.updateChecklistItemStatus(String(checklistId), newStatus);
      console.log(response.message);
    } catch (error) {
      console.error(`Failed to update status for checklist item ID ${checklistId}:`, error);

      // Revert the state if the API call fails
      setChecklistData((prevData) =>
        prevData.map((item) =>
          item.checklistid === checklistId ? { ...item, status: currentStatus } : item
        )
      );
    }
  };

  const handleDelete = async (checklistId: number | undefined): Promise<string> => {
    if (!checklistId) {
      console.error('Checklist ID is undefined');
      throw new Error('Invalid checklist ID');
    }

    try {
      // Using HTTP delete directly since deleteChecklistItem doesn't exist in the service
      // The backend endpoint is DELETE /delete_checklistitem/<int:item_id>
      const httpService = new HttpService();
      const result = await httpService.delete<{ message: string; sept_tenantid: string }>(
        `/delete_checklistitem/${checklistId}`
      );
      // Call the callback to refresh the checklist list
      if (onChecklistDeleted) {
        onChecklistDeleted();
      }
      return result.message || 'Checklist item deleted successfully';
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      throw error;
    }
  };

  const truncateTitle = (title: string | null | undefined): string => {
    if (!title || typeof title !== 'string') {
      return "";
    }
    const words = title.split(" ");
    if (words.length > 20) {
      return words.slice(0, 20).join(" ") + " ...";
    }
    return title;
  };

  if (!data) {
    return <></>;
  }

  const columns = [
    {
      header: 'Status',
      accessor: 'status' as keyof ChecklistItem,
      render: (row: ChecklistItem) => (
        <label className="checkbox-container">
          <input
            type="checkbox"
            checked={row.status || false}
            onChange={() => handleStatusToggle(row.checklistid, row.status || false)}
          />
          <span className="checkbox"></span>
        </label>
      )
    },
    {
      header: 'Description',
      accessor: 'description' as keyof ChecklistItem,
      render: (row: ChecklistItem) => (
        <button
          className="secondary-TableLink"
          title={row.description}
          onClick={() => openSidebar(row)}
        >
          {truncateTitle(row.description)}
        </button>
      )
    },
    {
      header: (
        <div className="due-date-header">
          <span>Matter ID</span>
        </div>
      ),
      accessor: 'matterid' as keyof ChecklistItem
    },
    {
      header: (
        <div className="due-date-header">
          <Link to={`${detailRouteBase()}/${matterId}/tasks/createChecklist`} className="add-icon-link">
            <FontAwesomeIcon icon={faPlus} className="icon-button" aria-label="Add" />
          </Link>
        </div>
      ),
      render: (row: ChecklistItem) => {
        console.log('Checklist row data:', row); // Debug log
        return (
          <Delete 
            onDelete={() => handleDelete(row.checklistid)}
            itemName="checklist item"
            size="medium"
            onSuccess={() => {}}
          />
        );
      }
    }
  ];

  const emptyAction = (
    <Link to={`${detailRouteBase()}/${matterId}/tasks/createChecklist`} className="text-link">
      <h3 style={{ textAlign: "center" }}>Click to add new checklist</h3>
    </Link>
  );

  return (
    <>
      <Table 
        columns={columns}
        data={checklistData}
        emptyMessage="No checklists found"
        emptyAction={emptyAction}
        rowKey="checklistid"
        pagination={pagination}
      />
      <ChecklistSideBar checklist={selectedChecklist} onClose={closeSidebar} isOpen={isSidebarOpen} />
    </>
  );
};

export default ChecklistTable;

